<script setup lang="ts">
import Icon from '@/components/ui/Icon.vue';
import { useToast } from '@/composables/useToast';

const { toasts, dismiss } = useToast();
</script>

<template>
  <div class="toaster" aria-live="polite">
    <transition-group name="toast">
      <div v-for="t in toasts" :key="t.id" :class="['toast', `toast-${t.kind}`]" role="status">
        <span class="toast-icon">
          <Icon :name="t.kind === 'error' ? 'alert' : 'check'" :size="16" :stroke-width="2" />
        </span>
        <span class="toast-msg">{{ t.message }}</span>
        <button type="button" class="toast-close" aria-label="Fermer" @click="dismiss(t.id)">
          <Icon name="x" :size="14" :stroke-width="2.2" />
        </button>
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.toaster {
  position: fixed;
  top: var(--space-16);
  right: var(--space-8);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: max-content;
  max-width: 380px;
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--color-text);
  pointer-events: auto;
}

.toast-icon {
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 1px;
}

.toast-error .toast-icon {
  color: var(--color-danger);
}

.toast-success .toast-icon {
  color: var(--color-success);
}

.toast-msg {
  flex: 1;
  line-height: 1.4;
}

.toast-close {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-1);
  margin: calc(-1 * var(--space-1));
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: color 120ms ease-out;
}

.toast-close:hover {
  color: var(--color-text);
}

.toast-close:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 240ms ease,
    transform 240ms ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
